import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface Analysis {
  id: string;
  sermon_id: string | null;
  title: string;
  content: string;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

interface CreateAnalysisData {
  sermon_id?: string;
  title: string;
  content: string;
}

export function useAnalysis() {
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const loadAnalyses = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: fetchError } = await supabase
        .from('sermon_analysis')
        .select('*')
        .eq('user_id', user.id)
        .eq('archived', showArchived)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setAnalyses(data || []);
    } catch (err) {
      console.error('Error loading analyses:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analyses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalyses();
  }, [showArchived]);

  const saveAnalysis = async (data: CreateAnalysisData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: savedAnalysis, error: saveError } = await supabase
        .from('sermon_analysis')
        .insert([{
          ...data,
          user_id: user.id
        }])
        .select()
        .single();

      if (saveError) throw saveError;
      await loadAnalyses();
      return savedAnalysis;
    } catch (err) {
      console.error('Error saving analysis:', err);
      throw err;
    }
  };

  const toggleArchive = async (id: string, currentState: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: updateError } = await supabase
        .from('sermon_analysis')
        .update({ archived: !currentState })
        .eq('id', id)
        .eq('user_id', user.id);

      if (updateError) throw updateError;
      await loadAnalyses();
    } catch (err) {
      console.error('Error toggling archive:', err);
      throw err;
    }
  };

  const deleteAnalysis = async (id: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: deleteError } = await supabase
        .from('sermon_analysis')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;
      await loadAnalyses();
    } catch (err) {
      console.error('Error deleting analysis:', err);
      throw err;
    }
  };

  return {
    analyses,
    loading,
    error,
    showArchived,
    setShowArchived,
    saveAnalysis,
    toggleArchive,
    deleteAnalysis,
    refresh: loadAnalyses
  };
}